import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical, ExternalLink, Edit2, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const PartnerSites = () => {
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [editingSite, setEditingSite] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    logo_url: '',
    description: '',
    is_active: true
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await axiosInstance.get('/admin/partner-sites');
      setSites(response.data);
    } catch (error) {
      console.error('Error fetching partner sites:', error);
      toast.error('Fehler beim Laden der Partner-Seiten');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axiosInstance.post('/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo hochgeladen');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingSite) {
        await axiosInstance.put(`/admin/partner-sites/${editingSite}`, formData);
        toast.success('Partner-Seite aktualisiert');
      } else {
        await axiosInstance.post('/admin/partner-sites', formData);
        toast.success('Partner-Seite hinzugefügt');
      }

      setFormData({
        name: '',
        url: '',
        logo_url: '',
        description: '',
        is_active: true
      });
      setEditingSite(null);
      setShowForm(false);
      fetchSites();
    } catch (error) {
      console.error('Error saving partner site:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleEdit = (site) => {
    setFormData({
      name: site.name,
      url: site.url,
      logo_url: site.logo_url || '',
      description: site.description || '',
      is_active: site.is_active
    });
    setEditingSite(site.id);
    setShowForm(true);
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm('Möchten Sie diese Partner-Seite wirklich löschen?')) return;

    try {
      await axiosInstance.delete(`/admin/partner-sites/${siteId}`);
      toast.success('Partner-Seite gelöscht');
      fetchSites();
    } catch (error) {
      console.error('Error deleting partner site:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggleActive = async (site) => {
    try {
      await axiosInstance.put(`/admin/partner-sites/${site.id}`, {
        is_active: !site.is_active
      });
      toast.success(site.is_active ? 'Deaktiviert' : 'Aktiviert');
      fetchSites();
    } catch (error) {
      console.error('Error toggling active status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: '',
      url: '',
      logo_url: '',
      description: '',
      is_active: true
    });
    setEditingSite(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-center py-12">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Partner-Seiten / Kooperationen</h1>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            {showForm ? 'Abbrechen' : 'Neue Partner-Seite'}
          </Button>
        </div>

        {/* Form */}
        {showForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{editingSite ? 'Partner-Seite bearbeiten' : 'Neue Partner-Seite hinzufügen'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Partner Shop"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="url">URL *</Label>
                  <Input
                    id="url"
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://partner-website.de"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Beschreibung (optional)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Kurze Beschreibung der Partner-Seite"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="logo">Logo (optional)</Label>
                  <div className="flex gap-4 items-center">
                    {formData.logo_url && (
                      <img
                        src={formData.logo_url}
                        alt="Logo Preview"
                        className="h-16 w-16 object-contain rounded border"
                      />
                    )}
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                    />
                  </div>
                  {uploadingLogo && <p className="text-sm text-gray-500 mt-1">Lädt hoch...</p>}
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Aktiv (auf Kundenseite anzeigen)</Label>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    Speichern
                  </Button>
                  {editingSite && (
                    <Button type="button" variant="outline" onClick={handleCancelEdit}>
                      <X className="h-4 w-4 mr-2" />
                      Abbrechen
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Sites List */}
        <div className="space-y-4">
          {sites.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-gray-600">Noch keine Partner-Seiten vorhanden</p>
                <p className="text-sm text-gray-500 mt-2">Klicken Sie auf "Neue Partner-Seite", um eine hinzuzufügen</p>
              </CardContent>
            </Card>
          ) : (
            sites.map((site) => (
              <Card key={site.id} className={!site.is_active ? 'opacity-60' : ''}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <GripVertical className="h-5 w-5 text-gray-400 cursor-move" />
                    
                    {site.logo_url && (
                      <img
                        src={site.logo_url}
                        alt={site.name}
                        className="h-12 w-12 object-contain rounded"
                      />
                    )}

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{site.name}</h3>
                        {!site.is_active && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                            Inaktiv
                          </span>
                        )}
                      </div>
                      <a
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {site.url}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      {site.description && (
                        <p className="text-sm text-gray-600 mt-1">{site.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={site.is_active}
                        onCheckedChange={() => handleToggleActive(site)}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleEdit(site)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDelete(site.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerSites;
